import { Element } from "@xmpp/xml"
import { Reference } from "../reducers/chatsSlice"
import {
  FinishedRouteAlert,
  Message,
  MessageDisplayType,
  MessageFallback,
  MessageType,
  ReferenceRouteAlert,
  StopLateAlert,
  Thumbnail,
} from "../reducers/messagesSlice"
import Route2gParser from "@/lib/route2gParser"
import { parse as jidParse } from "@/lib/cachedJid"
import { Toll } from "@/reducers/routes2gSlice"
import { ORIGINAL_BODY_NS } from "@/lib/originalBody"
import MessageHelpers from "@/lib/messageHelpers"
import { parseAiWidgetTacho } from "@/lib/aiWidgetTachoParser"
import { parseAiWidgetOrder } from "@/lib/aiWidgetOrderParser"

export type ParseOptions = {
  timestamp?: string
  toBeSent?: boolean
  forwarded?: boolean
  occupantJid?: (from: string) => string | undefined
}

function parseTolls(rawValue?: string): Toll[] {
  if (!rawValue || rawValue === "None" || rawValue === "null") {
    return []
  }

  try {
    const parsed = JSON.parse(rawValue)
    return Array.isArray(parsed) ? (parsed as Toll[]) : []
  } catch {
    return []
  }
}

class MessageParser {
  static parse(stanza: Element, options?: ParseOptions): Message {
    const timestamp = options?.timestamp
    const toBeSent = options?.toBeSent || false
    const forwarded = options?.forwarded || false

    if (!stanza.is("message")) {
      throw `Unable to parse non message stanza: ${stanza.toString()}`
    }

    return {
      id: MessageParser.id(stanza, toBeSent, forwarded),
      originId: MessageParser.originId(stanza),
      to: MessageParser.to(stanza),
      from: MessageParser.from(stanza),
      senderJid: MessageParser.senderJid(stanza, options?.occupantJid),
      author: MessageParser.author(stanza),
      type: MessageParser.type(stanza),
      body: MessageParser.body(stanza),
      translation: MessageParser.translation(stanza),
      originalBody: MessageParser.originalBody(stanza),
      originalBodyLang: MessageParser.originalBodyLang(stanza),
      timestamp: timestamp || new Date().toISOString(),
      thumbnail: MessageParser.thumbnail(stanza),
      file: MessageParser.file(stanza),
      url: MessageParser.url(stanza),
      fileName: MessageParser.fileName(stanza),
      forwarded: MessageParser.forwardedMessage(stanza, options),
      references: MessageParser.references(stanza),
      replaceId: MessageParser.replaceId(stanza),
      missedCallNumber: MessageParser.missedCallNumber(stanza),
      replyMessage: MessageParser.replyMessage(stanza),
      markers: [],
      reactions: [],
      displayType: MessageDisplayType.Message,
      active: MessageParser.active(stanza),
      activity: MessageParser.activity(stanza),
      composing: MessageParser.composing(stanza),
      paused: MessageParser.paused(stanza),
      internal: MessageParser.internal(stanza),
      thread: MessageParser.thread(stanza),
      agentChat: MessageParser.agentChat(stanza),
      fuelAlert: MessageParser.fuelAlert(stanza),
      cmrLinkRequest: MessageParser.cmrLinkRequest(stanza),
      referenceRouteAlert: MessageParser.referenceRouteAlert(stanza),
      stopLateAlert: MessageParser.stopLateAlert(
        stanza,
        timestamp || new Date().toISOString(),
      ),
      finishedRouteAlert: MessageParser.finishedRouteAlert(
        stanza,
        timestamp || new Date().toISOString(),
      ),
      ...MessageParser.carReport(stanza),
      ...MessageParser.aiWidgetTacho(stanza),
      ...MessageParser.aiWidgetOrder(stanza),
    }
  }

  static aiWidgetTacho(stanza: Element): Pick<Message, "aiWidgetTacho"> {
    const card = parseAiWidgetTacho(stanza)
    return card ? { aiWidgetTacho: card } : {}
  }

  static aiWidgetOrder(stanza: Element): Pick<Message, "aiWidgetOrder"> {
    const card = parseAiWidgetOrder(stanza)
    return card ? { aiWidgetOrder: card } : {}
  }

  static replaceId(stanza: Element): string | undefined {
    const replace = stanza.getChild("replace", "urn:xmpp:message-correct:0")

    if (!replace) return undefined

    return replace.attrs.id
  }

  static references(stanza: Element): Reference[] {
    const references = stanza.getChildren("reference", "urn:xmpp:reference:0")

    if (!references) return []

    // In case of a reply message, stanza body consists of two parts: fallback (body of a message we replied to)
    // and the actual text of the reply. In the UI only the body is displayed,
    // so position 0 of a message on our side is right after the end of the fallback.
    // That's why we need to substract that value (fallback length)
    // from the `begin` and `end` values of the reference to get the correct position in the UI
    const fallbackLength = Number(
      stanza
        .getChild("fallback", "urn:xmpp:fallback:0")
        ?.getChild("body")
        ?.getAttr("end") ?? 0,
    )

    return (
      references
        .map((reference) => {
          return {
            type: reference.attrs.type,
            begin: Number(reference.attrs.begin) - fallbackLength,
            end: Number(reference.attrs.end) - fallbackLength,
          }
        })
        // We don't want to display rederences to the fallback part
        .filter((reference) => reference.begin >= 0)
    )
  }

  /**
   * The reply-quote fallback element, whichever namespace it arrived in. The
   * offsets it carries apply to any body-shaped text on the stanza, since the
   * quote prefix is written identically into each of them.
   */
  private static replyFallback(stanza: Element) {
    return (
      stanza.getChild("fallback", "urn:xmpp:feature-fallback:0") ||
      stanza.getChild("fallback", "urn:xmpp:fallback:0")
    )
  }

  static body(stanza: Element) {
    const fallback = MessageParser.replyFallback(stanza)
    if (fallback) {
      const end = fallback.getChild("body")?.getAttr("end")
      return stanza.getChildText("body")?.slice(end) ?? ""
    }

    return stanza.getChildText("body") || ""
  }

  static active(stanza: Element): boolean {
    return !!stanza.getChild("active", "http://jabber.org/protocol/chatstates")
  }

  static composing(stanza: Element): boolean {
    return !!stanza.getChild(
      "composing",
      "http://jabber.org/protocol/chatstates",
    )
  }

  static internal(stanza: Element): boolean {
    return !!stanza.getChild("internal", "urn:slickshift:hints:0")
  }

  /**
   * The dispatcher↔Slick AI conversation. A separate stamp from `<thread>`,
   * which the backend also puts on the notes the whole office reads.
   */
  static agentChat(stanza: Element): boolean {
    return !!stanza.getChild("agent-chat", "urn:slickshift:hints:0")
  }

  static thread(stanza: Element): string | undefined {
    const value = stanza.getChild("thread")?.getText().trim()
    return value || undefined
  }

  static fuelAlert(stanza: Element): boolean {
    return (
      stanza.getChild("alert", "urn:slickshift:hints:0")?.getAttr("about") ===
      "fuel"
    )
  }

  static carReport(stanza: Element): Pick<Message, "carReport"> {
    const kind = stanza
      .getChild("car-report", "urn:slickshift:hints:0")
      ?.getAttr("kind")
    return kind === "fault" || kind === "collision" ? { carReport: kind } : {}
  }

  static cmrLinkRequest(
    stanza: Element,
  ): { fileUrl: string; fileName?: string } | undefined {
    const el = stanza.getChild(
      "cmr-link-request",
      "urn:slickshift:cmr-link-request:0",
    )
    if (!el) return undefined
    const fileUrl = el.attrs.file_url
    if (!fileUrl) return undefined
    return { fileUrl, fileName: el.attrs.file_name }
  }

  static referenceRouteAlert(stanza: Element): ReferenceRouteAlert | undefined {
    const alert = stanza.getChild("alert", "urn:slickshift:hints:0")

    if (!alert) return undefined

    const referenceRoute = stanza.getChild(
      "reference-route",
      "urn:slickshift:reference-route:0",
    )

    if (!referenceRoute) return undefined

    return {
      isOffRoute: referenceRoute.attrs.is_off_route === "1",
      previousIsOffRoute: referenceRoute.attrs.previous_is_off_route === "1",
      deltaMeters: Number(referenceRoute.attrs.delta_meters),
      updatedAt: referenceRoute.attrs.updated_at,
      isRouteDeltaAlert: referenceRoute.attrs.is_route_delta_alert === "1",
      alertId: referenceRoute.attrs.alert_id,
    }
  }

  static stopLateAlert(
    stanza: Element,
    timestamp: string,
  ): StopLateAlert | undefined {
    const alert = stanza.getChild("alert", "urn:slickshift:hints:0")

    if (!alert) return undefined

    const stopElement = stanza.getChild("stop", "urn:slickshift:route:0")

    if (!stopElement) return undefined

    let stop
    if (stopElement) {
      stop = Route2gParser.parseRouteStop(stopElement)
    }

    return {
      alertId: timestamp,
      stop,
    }
  }

  static finishedRouteAlert(
    stanza: Element,
    timestamp: string,
  ): FinishedRouteAlert | undefined {
    const alert = stanza.getChild("alert", "urn:slickshift:hints:0")

    if (!alert) return undefined

    const alertPayload = stanza.getChild(
      "alert-payload",
      "urn:slickshift:alert:payload:0",
    )

    if (!alertPayload) return undefined

    // Support both new and old namespaces
    const deliveryOrderFulfillment =
      alertPayload.getChild(
        "delivery-order-fulfillment",
        "urn:slickshift:delivery-order:0",
      ) ||
      alertPayload.getChild(
        "delivery-order-fulfillment",
        "urn:slickshift:delivery-order:fulfillment:0",
      )

    if (!deliveryOrderFulfillment) return undefined

    return {
      alertId: timestamp,
      startTimestamp: deliveryOrderFulfillment.attrs.start_timestamp,
      endTimestamp: deliveryOrderFulfillment.attrs.end_timestamp,
      actualDistance: Number(deliveryOrderFulfillment.attrs.actual_distance),
      actualDuration: Number(deliveryOrderFulfillment.attrs.actual_duration),
      routeTimestamp: deliveryOrderFulfillment.attrs.route_timestamp,
      firstStopId: deliveryOrderFulfillment.attrs.first_stop_id,
      lastStopId: deliveryOrderFulfillment.attrs.last_stop_id,
      expectedRouteDriverJid:
        deliveryOrderFulfillment.attrs.expected_route_driver_jid,
      expectedRouteTimestamp:
        deliveryOrderFulfillment.attrs.expected_route_timestamp,
      expectedRouteFirstStopId:
        deliveryOrderFulfillment.attrs.expected_route_first_stop_id,
      expectedRouteLastStopId:
        deliveryOrderFulfillment.attrs.expected_route_last_stop_id,
      actualApproachDistance: Number(
        deliveryOrderFulfillment.attrs.actual_approach_distance,
      ),
      actualApproachDuration: Number(
        deliveryOrderFulfillment.attrs.actual_approach_duration,
      ),
      expectedTolls: parseTolls(deliveryOrderFulfillment.attrs.expected_tolls),
      actualTolls: parseTolls(deliveryOrderFulfillment.attrs.actual_tolls),
      actualApproachTolls: parseTolls(
        deliveryOrderFulfillment.attrs.actual_approach_tolls,
      ),
      expectedDistance: Number(
        deliveryOrderFulfillment.attrs.expected_distance,
      ),
      expectedDuration: Number(
        deliveryOrderFulfillment.attrs.expected_duration,
      ),
      fileName: deliveryOrderFulfillment.attrs.file_name,
      fileUrl: deliveryOrderFulfillment.attrs.file_url,
      transportNumber: deliveryOrderFulfillment.attrs.transport_number,
    }
  }

  static activity(stanza: Element): string | undefined {
    return stanza
      .getChild("activity", "urn:slickshift:composing-activity:0")
      ?.getText()
  }

  static paused(stanza: Element): boolean {
    return !!stanza.getChild("paused", "http://jabber.org/protocol/chatstates")
  }

  static translation(stanza: Element) {
    return stanza.getChildren("body").length > 1
      ? stanza.getChildren("body")[1].getText()
      : undefined
  }

  static originalBody(stanza: Element): string | undefined {
    const original = stanza.getChild("original", ORIGINAL_BODY_NS)
    if (!original) return undefined

    // Replies write <original> as `${replyFallback}${text}`, exactly like
    // <body>, so the urn:xmpp:fallback:0 offsets stay valid for both. Strip
    // the quote here the same way body() does — otherwise the quote is shown
    // twice: once as the rendered quote card, once as plain text on the
    // original line underneath it.
    const fallback = MessageParser.replyFallback(stanza)
    if (fallback) {
      const end = fallback.getChild("body")?.getAttr("end")
      return original.getText().slice(end) || undefined
    }

    return original.getText() || undefined
  }

  static originalBodyLang(stanza: Element): string | undefined {
    return stanza.getChild("original", ORIGINAL_BODY_NS)?.attrs.lang
  }

  static to(stanza: Element): string {
    return stanza.attrs.to
  }

  static from(stanza: Element): string {
    return stanza.attrs.from
  }

  static senderJid(
    stanza: Element,
    occupantJid?: ParseOptions["occupantJid"],
  ): string | undefined {
    try {
      if (MessageHelpers.isFromForwarder({ from: stanza.attrs.from }))
        return undefined
    } catch {
      return undefined
    }
    const realJid = stanza
      .getChild("x", "http://jabber.org/protocol/muc#user")
      ?.getChild("item")?.attrs.jid
    if (realJid) {
      try {
        return jidParse(realJid).bare().toString()
      } catch {
        return occupantJid?.(stanza.attrs.from)
      }
    }
    return occupantJid?.(stanza.attrs.from)
  }

  static author(stanza: Element): string | undefined {
    const author = stanza.getChild("author", "urn:slickshift:author")
    if (author) return author.getText()
    return undefined
  }

  static type(stanza: Element): MessageType {
    return stanza.attrs.type || "chat"
  }

  static id(
    stanza: Element,
    toBeSent: boolean,
    forwarded: boolean = false,
  ): string {
    if (MessageParser.type(stanza) === "groupchat") {
      const stanzaId = stanza.getChild("stanza-id")

      if (!stanzaId && !toBeSent && !forwarded) {
        throw "Group chat message must have stanza-id included"
      }
      if (stanzaId) return stanzaId.attrs.id
    }

    return stanza.attrs.id
  }

  static originId(stanza: Element): string | undefined {
    const originId = stanza.getChild("origin-id")
    if (originId) return originId.attrs.id

    return undefined
  }

  static file(stanza: Element): boolean {
    return !!MessageParser.url(stanza)
  }

  static url(stanza: Element): string | undefined {
    return (
      stanza
        .getChild("reference", "urn:xmpp:reference:0")
        ?.getChild("media-sharing", "urn:xmpp:sims:1")
        ?.getChild("sources")
        ?.getChild("reference")?.attrs.uri || undefined
    )
  }

  static fileName(stanza: Element): string | undefined {
    return (
      stanza
        .getChild("reference", "urn:xmpp:reference:0")
        ?.getChild("media-sharing", "urn:xmpp:sims:1")
        ?.getChild("file")
        ?.getChildText("name") || undefined
    )
  }

  static thumbnail(stanza: Element): Thumbnail | undefined {
    const thumbnail = stanza
      .getChild("reference", "urn:xmpp:reference:0")
      ?.getChild("media-sharing", "urn:xmpp:sims:1")
      ?.getChild("file")
      ?.getChild("thumbnail")
    if (thumbnail) {
      return {
        width: parseInt(thumbnail.getAttr("width"), 10),
        height: parseInt(thumbnail.getAttr("height"), 10),
        uri: thumbnail.getAttr("uri"),
      }
    }
  }

  static forwardedMessage(
    stanza: Element,
    options?: ParseOptions,
  ): Message | undefined {
    const message = stanza.getChild("forwarded")?.getChild("message")

    if (!message) return undefined

    return MessageParser.parse(message, { ...options, forwarded: true })
  }

  static missedCallNumber(stanza: Element): string | undefined {
    return stanza
      .getChild("missed-call", "urn:slickshift:missed-call:0")
      ?.getAttr("tel")
  }

  static replyMessage(stanza: Element): MessageFallback | undefined {
    const reply = stanza.getChild("reply", "urn:xmpp:reply:0")
    const fallback = stanza.getChild("fallback", "urn:xmpp:fallback:0")
    if (!reply || !fallback) return

    const body = (
      stanza
        .getChildText("body")
        ?.slice(0, fallback.getChild("body")?.getAttr("end")) ?? ""
    ).trim()

    const [author, ...rest] = body.split("\n").map((line) => line.slice(2))

    return {
      id: String(reply.getAttr("id")),
      author:
        author.match(/(.*) wrote:/)?.[1] || author.match(/(.*):/)?.[1] || "",
      body: rest.join("\n"),
    }
  }
}

export default MessageParser
